import { ApiTab } from '@/Enums/Attribution/ApiTabs.enum';
import { CustomReportChannel } from '@/Enums/ReportBuilder/ReportChannel.enum';
import ReportingDashboardApiTab from '@/Enums/ReportingDashboard/ApiTab.enum';
import ApiTabQueryParam from '@/Enums/ReportingDashboard/ApiTabQueryParam.enum';
import BillingModelService from '@/Services/Whitelabels/BillingModel.service';
import { isSuperAdminRole } from '@/helpers/roles';
import type { RootState } from '@/store';
import { selectMeRole } from '@/store/selectors/me.selectors';
import { createSelector } from '@reduxjs/toolkit';

export const selectCurrentWhitelabel = (state: RootState) => state.currentWhitelabel;

export const selectCurrentWhitelabelWhitelabel = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel, whitelabelRequestId }) => ({
    whitelabel,
    loadingCurrentWhitelabel: !!whitelabelRequestId,
  }),
);

export const selectCurrentWhitelabelSettings = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.settings,
);

export const selectExtendedAudiences = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.extendedAudiences,
);

export const selectExtendedReportingDashboardFilters = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.extendedReportingDashboardFilters,
);

export const selectWhitelabelTableSettings = createSelector([selectCurrentWhitelabel], ({ whitelabel }) => ({
  showCpm: whitelabel.showCpm,
  showLineItemAlternativeId: whitelabel.showLineItemAlternativeId,
  showCampaignAlternativeId: whitelabel.showCampaignAlternativeId,
  showAdvertiserAlternativeId: whitelabel.showAdvertiserAlternativeId,
  showClientSpend: whitelabel.showClientSpendInReportingDashboards,
  defaultOmnichannelGridReport: whitelabel.defaultOmnichannelGripReport,
  defaultAudioGridReport: whitelabel.defaultAudioGridReport,
}));

export const selectWhitelabelBillingModel = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.billingModel,
);

export const selectIsPresetsMode = createSelector([selectWhitelabelBillingModel], billingModel =>
  BillingModelService.isPercentOfMedia(billingModel),
);

export const selectShowBidCpmControl = createSelector([selectWhitelabelBillingModel], billingModel =>
  BillingModelService.allowBidCpmControl(billingModel),
);

export const selectShowBidCpmGuidance = createSelector(
  [selectCurrentWhitelabel, selectWhitelabelBillingModel],
  ({ whitelabel }) => whitelabel.showBidCpmGuidance,
);

export const selectShowBidCpmGuidanceInForecaster = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.showBidCpmGuidanceForecaster,
);

export const selectShowRateCardCpmControl = createSelector([selectWhitelabelBillingModel], billingModel =>
  BillingModelService.allowRateCardCpmControl(billingModel),
);

export const selectAllowDeviceControl = createSelector([selectWhitelabelBillingModel], billingModel =>
  BillingModelService.allowDeviceSelection(billingModel),
);

export const selectVisibleReportingScreenTabs = createSelector([selectCurrentWhitelabel], ({ whitelabel }) => {
  const tabsOrder = [
    ReportingDashboardApiTab.Omnichannel,
    ReportingDashboardApiTab.Google,
    ReportingDashboardApiTab.Adswizz,
    ReportingDashboardApiTab.Dooh,
    ReportingDashboardApiTab.Search,
    ReportingDashboardApiTab.Social,
    ReportingDashboardApiTab.Simplifi,
    ReportingDashboardApiTab.Gam,
  ];
  // Drop any tab the frontend doesn't know about (e.g. a backend-only "GAM"
  // value that ships before the tab lands in `tabsOrder`). Such a value has
  // `tabsOrder.indexOf() === -1`, so it would sort to position 0 and shift
  // every real tab's index up by one — desyncing the URL-param mapping
  // (`selectReportingDashboardApiTabIndices`, indexOf-based) from the rendered
  // MUI tab positions and mis-routing clicks (Simpli.fi → `tab=dooh`).
  return [...whitelabel.visibleReportingScreenTabs]
    .filter(tab => tabsOrder.includes(tab))
    .sort((a, b) => tabsOrder.indexOf(a) - tabsOrder.indexOf(b));
});

const INSIGHTS_TABS_ORDER = [ApiTab.WebVisits, ApiTab.OfflineSales, ApiTab.FootTraffic];

export const selectVisibleInsightsTabs = createSelector([selectCurrentWhitelabel], ({ whitelabel }) => {
  const tabs = whitelabel.visibleInsightsTabs ?? INSIGHTS_TABS_ORDER;
  return INSIGHTS_TABS_ORDER.filter(tab => tabs.includes(tab));
});

export const selectReportingDashboardApiTabIndices = createSelector(
  [selectVisibleReportingScreenTabs],
  visibleReportingScreenTabs =>
    Object.fromEntries(
      Object.values(ReportingDashboardApiTab).map(tab => [tab, (visibleReportingScreenTabs ?? []).indexOf(tab) ?? -1]),
    ) as Record<ReportingDashboardApiTab, number>,
);

export const selectReportingQueryParamsApiTabIndices = createSelector(
  [selectReportingDashboardApiTabIndices],
  reportingDashboardApiTabIndices => ({
    [ApiTabQueryParam.Omnichannel]: reportingDashboardApiTabIndices[ReportingDashboardApiTab.Omnichannel],
    [ApiTabQueryParam.Google]: reportingDashboardApiTabIndices[ReportingDashboardApiTab.Google],
    [ApiTabQueryParam.Adswizz]: reportingDashboardApiTabIndices[ReportingDashboardApiTab.Adswizz],
    [ApiTabQueryParam.Dooh]: reportingDashboardApiTabIndices[ReportingDashboardApiTab.Dooh],
    [ApiTabQueryParam.Search]: reportingDashboardApiTabIndices[ReportingDashboardApiTab.Search],
    [ApiTabQueryParam.Social]: reportingDashboardApiTabIndices[ReportingDashboardApiTab.Social],
    [ApiTabQueryParam.Simplifi]: reportingDashboardApiTabIndices[ReportingDashboardApiTab.Simplifi],
    [ApiTabQueryParam.Gam]: reportingDashboardApiTabIndices[ReportingDashboardApiTab.Gam],
  }),
);

export const selectVisibleReportBuilderBeeswaxDimensions = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => {
    return whitelabel.visibleReportBuilderBeeswaxDimensions;
  },
);

const REPORT_BUILDER_TABS_ORDER = [
  CustomReportChannel.Beeswax,
  CustomReportChannel.Youtube,
  CustomReportChannel.Omniplatform,
];

export const selectVisibleReportBuilderTabs = createSelector([selectCurrentWhitelabel], ({ whitelabel }) => {
  const tabs = whitelabel.visibleReportBuilderTabs ?? REPORT_BUILDER_TABS_ORDER;
  return REPORT_BUILDER_TABS_ORDER.filter(tab => tabs.includes(tab));
});

// `id` is 0 until /whitelabel/current resolves, so an empty tab list before that
// means "not loaded yet", not "this org has no tabs".
export const selectIsCurrentWhitelabelLoaded = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.id !== 0,
);

export const selectWhitelabelData = createSelector([selectCurrentWhitelabel], ({ whitelabel }) => whitelabel);

export const selectShowReachInForecaster = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.showReachInForecaster,
);

export const selectVisibleForecasterChannels = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.visibleForecasterChannels,
);

export const selectVisibleCampaignManagementChannels = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.visibleCampaignManagementChannels,
);

export const selectHasGamAccount = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => !!whitelabel.hasGamAccount,
);

export const selectBeeswaxImpressionsByTypeTableTabs = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.beeswaxImpressionsByTypeTableTabs,
);

export const selectIsDV360CampaignManagementEnabled = createSelector(
  [selectCurrentWhitelabel, selectMeRole],
  ({ whitelabel }, role) => whitelabel.dv360CampaignManagement || isSuperAdminRole(role),
);

export const selectIsZendeskWidgetEnabled = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.isZendeskWidgetEnabled,
);

export const selectIsRequestAudienceButtonHidden = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.hideRequestAudienceButton,
);

export const selectIsRequestProductButtonHidden = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.hideRequestProductButton,
);

export const selectIsProductSelectionCardHidden = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.hideProductSelectionCard,
);

export const selectIsAiReportingHidden = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.hideAiReporting,
);

export const selectIsAiForecasterHidden = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.hideAiForecaster,
);

export const selectIsAiPacingHidden = createSelector(
  [selectCurrentWhitelabel],
  ({ whitelabel }) => whitelabel.hideAiPacing,
);

export const selectHiddenImpressionsByTypeMetrics = createSelector([selectCurrentWhitelabel], ({ whitelabel }) => ({
  hideDeliveryCtr: whitelabel.hideCtrInDeliveryImpressionsByType,
  hideDeliveryAudioCompletePercent: whitelabel.hideAudioCompletePercentInDeliveryImpressionsByType,
  hideAttributionClicks: whitelabel.hideClicksInAttributionImpressionsByType,
}));
